import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { deep } from 'app/shared/lib/deep';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { fireEvents } from 'app/shared/lib/fire-events';
import { Toaster } from 'app/shared/lib/toaster';
import { ProviderLocationSelectCustomEvent } from 'app/cd/components/providers/provider-location-search';
import { IProviderSearchConfig } from 'app/cd/models/essette-interfaces';
import { AuthProviderReceivingReloadEvent, IAuthChangeCustomEventDetail } from 'app/um/lib/events';
import { IProviderSearchOptions } from 'app/cd/elements/providers/provider-search-filters';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { AuthContext, IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { EventAggregator } from 'aurelia-event-aggregator';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class AuthProvider extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull
    public key: string | null = null;
    @bindable.objectNull
    public providerInfo: CD.Provider | null = null;
    @bindable.object
    public providerSettings: IAuthContextSettingsDetails = {
        display: '',
        reqDataEntry: false,
    };
    @bindable.objectNull
    public auth: UM.Auth | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public reloadRequesting: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.fromView })
    public infoLoading: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView, changeHandler: 'canSearchNonContractedProvidersChanged' })
    public canSearchNonContractedProviders = true;

    @bindable.arrayNull()
    protected commonProviders: ReadonlyArray<UM.CommonProvider> = [];
    @bindable.array()
    protected regions: Core.AddressRegion[] = [];
    @bindable.array()
    protected genders: Core.Gender[] = [];
    protected options: IProviderSearchOptions | null = null;

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly authService: AuthService,
        private readonly toaster: Toaster,
        private readonly context: AuthContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.key) { throw new Error('Missing required object: key'); }

        this.options = {
            isContracted: {
                isVisible: true,
                isLocked: !this.canSearchNonContractedProviders,
                defaultValue: !!getApiSetting(this.context.apiSettings, 'provider.search.underContract.$default')
            }
        };
    }


    ////****************** Events ***************************************************////
    protected providerSelected(event: ProviderLocationSelectCustomEvent): void {
        if(event.detail?.providerLocation && this.key) {
            const key = `${this.key}.location.id`;
            this.saveAuthProvider(key, event.detail.providerLocation.id!);
        }
    }

    protected providerCleared(): void {
        if(this.key && this.auth?.id) {
            const key = `${this.key}.location`;
            this.saveAuthProvider(key, null);
        }
    }

    protected async canSearchNonContractedProvidersChanged(): Promise<void> {
        this.options = {
            isContracted: {
                isVisible: true,
                isLocked: !this.canSearchNonContractedProviders,
                defaultValue: !!getApiSetting(this.context.apiSettings, 'provider.search.underContract.$default')
            }
        };
    }

    ////****************** Functions ************************************************////
    protected getSearchConfig(): IProviderSearchConfig {
        return {
            contractedSearchUrl: `um/auths/${this.auth?.id}/contracted/providers/locations`
        } as IProviderSearchConfig;
    }

    protected filterProviders = (provider: UM.CommonProvider): boolean => {
        if(!provider.providerType?.key) return false;
        return provider.providerType!.key === this.key;
    }

    protected async updateAuthProvider(selectedProvider: UM.CommonProvider): Promise<void> {
        if(!!selectedProvider.providerType?.key && !!selectedProvider.providerLocation) {
            const key = `${selectedProvider.providerType.key}.location.id`;
            this.saveAuthProvider(key, selectedProvider.providerLocation.id!);
        }
    }

    private saveAuthProvider(key: string, id: string | null): void {
        if(!this.auth?.id) return;

        const editProviderAuth: UM.Auth = {
            id: this.auth.id
        };
        const editAuthProviders = deep(editProviderAuth);
        editAuthProviders[key] = id;

        this.saveAuth(editAuthProviders);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveAuth(editAuth: UM.Auth): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.saveAuth(editAuth);
            isSuccessful = response.isSuccessful;
            if(this.$element && this.auth) {
                await this.context.refreshAuthSettings();
                const detail: IAuthChangeCustomEventDetail = { authReloaded: true, triggerRules: true };
                fireEvents(this.$element, ['auth-change'], detail);
                if(this.reloadRequesting) this.events.publish(new AuthProviderReceivingReloadEvent());
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.$', isSuccessful);
            this.state.endSave();
        }
    }
}
